import { Button, Checkbox, DatePicker, Divider, Form, Input, message, Select, Tag } from 'antd';
import * as Component from 'components';
import { AGE_RATING, DUBBING_MODE, STATUS } from 'constants/constant';
import { contentService } from 'content/service/contentService';
import { SeriesInterface } from 'content/type/seriesType';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { genreApiService } from 'service/api/genreApiService';
import { peopleApiService } from 'service/api/peopleApiService';
import { seasonApiService } from 'service/api/seasonApiService';
import { seriesApiService } from 'service/api/seriesApiService';
import { GenreInterface } from 'type/genreType';
import { PeopleInterface } from 'type/peopleType';
import { SeasonData, SeasonInterface } from 'type/seasonType';
import CustomInputNumber from '../../../../../shared/components/CustomInputNumber';
import { EnhancedCastEditor } from '../../../../../shared/components/enhanced-cast';
import { I18nFieldData } from '../../../../../shared/types/i18n/i18nTypes';
import { seasonService } from '../../services/seasonService';

// ODK Meta Tag Display Component
interface OdkMetaTagDisplayProps {
  data?: {
    '#text': string;
    '@lang': string;
    '@type': string;
  }[];
}

// Weekday Checkbox Component
interface WeekdayCheckboxProps {
  value?: string;
  onChange?: (value: string) => void;
}

const WeekdayCheckbox: React.FC<WeekdayCheckboxProps> = ({ value, onChange }) => {
  const weekdays = [
    { label: 'Monday', value: 'monday' },
    { label: 'Tuesday', value: 'tuesday' },
    { label: 'Wednesday', value: 'wednesday' },
    { label: 'Thursday', value: 'thursday' },
    { label: 'Friday', value: 'friday' },
    { label: 'Saturday', value: 'saturday' },
    { label: 'Sunday', value: 'sunday' },
  ];

  // 문자열을 배열로 변환 (예: "monday,tuesday" -> ["monday", "tuesday"])
  const checkedValues = value ? value.split(',').filter(v => v.trim()) : [];

  const handleChange = (checkedList: string[]) => {
    // 배열을 문자열로 변환 (예: ["monday", "tuesday"] -> "monday,tuesday")
    const newValue = checkedList.length > 0 ? checkedList.join(',') : '';
    onChange?.(newValue);
  };

  return (
    <Checkbox.Group
      options={weekdays}
      value={checkedValues}
      onChange={handleChange}
      style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}
    />
  );
};

// Editable ODK Meta Tag Component
interface EditableOdkMetaTagProps {
  value?: {
    '#text': string;
    '@lang': string;
    '@type': string;
  }[];
  onChange?: (
    value: {
      '#text': string;
      '@lang': string;
      '@type': string;
    }[]
  ) => void;
}

const EditableOdkMetaTag: React.FC<EditableOdkMetaTagProps> = ({ value = [], onChange }) => {
  // 8개 고정 카테고리 정의
  const fixedCategories = [
    { type: 'tag', lang: 'ko', label: 'Tag (Korean)' },
    { type: 'tag', lang: 'en', label: 'Tag (English)' },
    { type: 'actor', lang: 'ko', label: 'Actor (Korean)' },
    { type: 'actor', lang: 'en', label: 'Actor (English)' },
    { type: 'director', lang: 'ko', label: 'Director (Korean)' },
    { type: 'director', lang: 'en', label: 'Director (English)' },
    { type: 'movie_genre', lang: 'ko', label: 'Genre (Korean)' },
    { type: 'movie_genre', lang: 'en', label: 'Genre (English)' },
  ];

  // 현재 값을 카테고리별로 그룹핑
  const getValuesByCategory = (type: string, lang: string): string[] => {
    if (!value || !Array.isArray(value)) {
      return [];
    }

    return value
      .filter(item => item['@type'] === type && item['@lang'] === lang)
      .map(item => item['#text']);
  };

  // 카테고리별 값 업데이트
  const updateCategoryValues = (type: string, lang: string, newValues: string[]) => {
    // 해당 카테고리의 기존 값들 제거
    const filteredValue = (value || []).filter(
      item => !(item['@type'] === type && item['@lang'] === lang)
    );

    // 새로운 값들 추가
    const newItems = newValues
      .filter(text => text.trim()) // 빈 문자열 제거
      .map(text => ({
        '#text': text.trim(),
        '@lang': lang,
        '@type': type,
      }));

    const updatedValue = [...filteredValue, ...newItems];
    onChange?.(updatedValue);
  };

  return (
    <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '12px' }}>
      {fixedCategories.map(({ type, lang, label }) => {
        const categoryValues = getValuesByCategory(type, lang);

        return (
          <div key={`${type}_${lang}`} style={{ marginBottom: '16px' }}>
            <div style={{ marginBottom: '8px' }}>
              <Tag color="blue" style={{ fontSize: '12px', padding: '2px 8px' }}>
                {label}
              </Tag>
            </div>
            <Select
              mode="tags"
              style={{ width: '100%' }}
              placeholder={`${label} Enter a value`}
              value={categoryValues}
              onChange={(newValues: string[]) => updateCategoryValues(type, lang, newValues)}
              tokenSeparators={[',']}
            />
          </div>
        );
      })}
    </div>
  );
};

const SeasonDetail: React.FC = () => {
  const [form] = Form.useForm();
  const { id } = useParams<{ id: string }>();
  const [searchedPeopleList, setSearchedPeopleList] = useState<PeopleInterface[]>([]);
  const [series, setSeries] = useState<SeriesInterface | null>(null);
  const [genreList, setGenreList] = useState<GenreInterface[]>([]);
  const [selectedGenres, setSelectedGenres] = useState<number[]>([]);
  const [initialGenreList, setInitialGenreList] = useState<GenreInterface[]>([]);

  // Enhanced Cast 상태 관리
  const [enhancedCast, setEnhancedCast] = useState<{ cast: number; type: string }[]>([]);

  // i18n 필드 상태 관리
  const [i18nTitle, setI18nTitle] = useState<I18nFieldData>({});
  const [i18nSynopsis, setI18nSynopsis] = useState<I18nFieldData>({});
  const [i18nSecondaryTitle, setI18nSecondaryTitle] = useState<I18nFieldData>({});
  const [i18nSeoTitle, setI18nSeoTitle] = useState<I18nFieldData>({});
  const [i18nSeoSynopsis, setI18nSeoSynopsis] = useState<I18nFieldData>({});
  const [i18nSeoKeyword, setI18nSeoKeyword] = useState<I18nFieldData>({});

  // i18n 필드 변경 핸들러 (content 페이지 방식 참고)
  const handleI18nFieldChange = useCallback(
    (fieldName: 'title' | 'synopsis' | 'secondary_title', newValue: I18nFieldData) => {
      if (fieldName === 'title') {
        setI18nTitle(newValue);
      } else if (fieldName === 'synopsis') {
        setI18nSynopsis(newValue);
        // 레거시 필드와 동기화
      } else if (fieldName === 'secondary_title') {
        setI18nSecondaryTitle(newValue);
      }
    },
    [i18nTitle, i18nSynopsis, i18nSecondaryTitle]
  );

  useEffect(() => {
    const fetchSeason = async (): Promise<void> => {
      try {
        // i18n 필드를 포함한 Season 데이터 조회
        const season: SeasonData = await seasonService.fetchSeasonWithI18n(Number(id));

        if (season.series) {
          const series: SeriesInterface = await seriesApiService.fetchById(Number(season.series));
          setSeries(series);
        }

        const formattedSeason = {
          ...season,
          release_date_start: season.release_date_start ? dayjs(season.release_date_start) : null,
          release_date_end: season.release_date_end ? dayjs(season.release_date_end) : null,
        };

        form.setFieldsValue(formattedSeason);

        // i18n 필드 초기화
        if (season.i18n_title) {
          setI18nTitle(season.i18n_title);
        }

        if (season.i18n_secondary_title) {
          setI18nSecondaryTitle(season.i18n_secondary_title);
        }

        if (season.i18n_synopsis) {
          setI18nSynopsis(season.i18n_synopsis);
        }

        if (season.i18n_seo_title) {
          setI18nSeoTitle(season.i18n_seo_title);
        }

        if (season.i18n_seo_synopsis) {
          setI18nSeoSynopsis(season.i18n_seo_synopsis);
        }

        if (season.i18n_seo_keyword) {
          setI18nSeoKeyword(season.i18n_seo_keyword);
        }

        // Season의 genres 값을 selectedGenres state에 설정
        if (season.genres) {
          setSelectedGenres(season.genres);
        }

        // Enhanced Cast 데이터 초기화
        if (season.enhanced_cast) {
          const enhancedCastData = season.enhanced_cast.map((item: any) => ({
            cast: item.cast,
            type: item.type,
          }));
          setEnhancedCast(enhancedCastData);
        }

        if (season.contents) {
          const contents = await contentService.fetchByIds(season.contents.join(','));
          form.setFieldValue(
            'contents',
            contents.results.map(content => {
              return { ...content, name: content.title };
            }) || []
          );
        }
      } catch (error) {
        console.error('Failed to fetch season with i18n:', error);
        // 폴백으로 기존 API 사용
        const season: SeasonInterface = await seasonApiService.fetchById(Number(id));
        if (season.series) {
          const series: SeriesInterface = await seriesApiService.fetchById(Number(season.series));
          setSeries(series);
        }

        const formattedSeason = {
          ...season,
          release_date_start: season.release_date_start ? dayjs(season.release_date_start) : null,
          release_date_end: season.release_date_end ? dayjs(season.release_date_end) : null,
        };

        form.setFieldsValue(formattedSeason);

        // 레거시 필드에서 i18n 필드로 변환
        setI18nTitle({
          ko: season.title || '',
          en: season.title_en || '',
        });

        setI18nSynopsis({
          ko: season.synopsis || '',
          en: season.synopsis_en || '',
        });

        if (season.genres) {
          setSelectedGenres(season.genres);
        }

        // Enhanced Cast 데이터 초기화 (폴백)
        if (season.enhanced_cast) {
          const enhancedCastData = season.enhanced_cast.map((item: any) => ({
            cast: item.cast,
            type: item.type,
          }));
          setEnhancedCast(enhancedCastData);
        }

        if (season.contents) {
          const contents = await contentService.fetchByIds(season.contents.join(','));
          form.setFieldValue(
            'contents',
            contents.results.map(content => {
              return { ...content, name: content.title };
            }) || []
          );
        }
      }
    };
    const seasonPeople = async (): Promise<void> => {
      const response = await peopleApiService.fetch(
        new URLSearchParams({
          page: '1',
          page_size: '100',
          seasons__id: id || '',
        })
      );
      setSearchedPeopleList(response.results);
    };

    const initGenres = async (): Promise<void> => {
      if (!id) return;

      // Season에 연결된 Genre들 가져오기
      const seasonGenreList = await genreApiService.fetch(
        new URLSearchParams({
          page: '1',
          page_size: '100',
          seasons__id: id,
        })
      );
      if (seasonGenreList) {
        setSelectedGenres(seasonGenreList.results.map((genre: GenreInterface) => genre.id));
      }

      // 전체 Genre 목록 가져오기
      const allGenreList = await genreApiService.fetch(
        new URLSearchParams({
          page: '1',
          page_size: '100',
        })
      );

      const combined = [...seasonGenreList.results, ...allGenreList.results];
      const uniqueGenres = combined.filter((v, i, a) => a.findIndex(t => t.id === v.id) === i);

      setGenreList(uniqueGenres);
      setInitialGenreList(uniqueGenres);
    };

    fetchSeason();
    seasonPeople();
    initGenres();
  }, [id]);

  const searchGenres = async (value: string): Promise<void> => {
    const response = await genreApiService.fetch(
      new URLSearchParams({
        page: '1',
        page_size: '100',
        search: value,
      })
    );
    setGenreList(response.results);
  };

  // Enhanced Cast를 위한 people 검색 핸들러
  const searchPeopleForEnhancedCast = async (value: string): Promise<void> => {
    if (!value.trim()) return;

    const response = await peopleApiService.fetch(
      new URLSearchParams({
        page: '1',
        page_size: '20',
        search: value,
      })
    );
    setSearchedPeopleList(response.results);
  };

  // i18n 데이터를 포함한 업데이트 핸들러
  const handleUpdateSeasonWithI18n = async () => {
    try {
      await form.validateFields();
      const formData = form.getFieldsValue();
      const seasonDataWithI18n = {
        ...formData,
        i18n_title: i18nTitle,
        i18n_synopsis: i18nSynopsis,
        i18n_secondary_title: i18nSecondaryTitle,
        i18n_seo_title: i18nSeoTitle,
        i18n_seo_synopsis: i18nSeoSynopsis,
        i18n_seo_keyword: i18nSeoKeyword,
        // Enhanced Cast 데이터 추가
        enhanced_cast: enhancedCast,
        // 날짜 포맷팅
        release_date_start: formData.release_date_start
          ? formData.release_date_start.format('YYYY-MM-DD')
          : null,
        release_date_end: formData.release_date_end
          ? formData.release_date_end.format('YYYY-MM-DD')
          : null,
      };

      // i18n 지원 업데이트 API 호출
      await seasonService.updateSeasonWithI18n(Number(id), seasonDataWithI18n);
      message.success('Season updated successfully');
      window.location.reload();
    } catch (error) {
      console.error('Failed to update season:', error);
      message.error('Failed to update season');
    }
  };

  return (
    <div>
      <div className="season-details-page">
        <h1>Season</h1>
        <Form form={form} layout="vertical" className="season-details-form">
          {/* 다국어 제목 입력 */}
          <Component.I18nFormFields
            fieldName="title"
            required={true}
            fieldType="input"
            value={i18nTitle}
            onChange={value => handleI18nFieldChange('title', value)}
          />

          <div style={{ marginTop: '16px' }}>
            <Component.I18nFormFields
              fieldName="secondary_title"
              required={false}
              fieldType="input"
              value={i18nSecondaryTitle}
              onChange={value => handleI18nFieldChange('secondary_title', value)}
            />
          </div>

          <div style={{ marginTop: '16px' }}>
            <Component.I18nFormFields
              fieldName="synopsis"
              required={false}
              fieldType="textarea"
              value={i18nSynopsis}
              onChange={value => handleI18nFieldChange('synopsis', value)}
            />
          </div>

          <Form.Item
            label="Slug"
            name="slug"
            rules={[{ required: true, message: 'Please enter a slug' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="Status" name="status">
            <Select>
              {STATUS.map(status => (
                <Select.Option key={status} value={status}>
                  {status}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Dubbing Mode" name="dubbing_mode">
            <Select>
              {Object.entries(DUBBING_MODE).map(([key, value]) => (
                <Select.Option key={key} value={key}>
                  {value}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="season_order" label="Season Order">
            <Input type="number" />
          </Form.Item>
          <Form.Item label="Age Rating" name="age_rating">
            <Select>
              {AGE_RATING.map(ageRating => (
                <Select.Option key={ageRating} value={ageRating}>
                  {ageRating}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="release_date_start" label="Release Start Date">
            <DatePicker />
          </Form.Item>
          <Form.Item name="release_date_end" label="Release End Date">
            <DatePicker />
          </Form.Item>
          {/* <Form.Item
            name="cast"
            label="Cast"
            rules={[{ required: false, message: 'Search and select Cast' }]}
          >
            <Select
              mode="multiple"
              showSearch
              placeholder="Search and select Cast"
              value={searchedPeopleList}
              onChange={value => {
                setSearchedPeopleList(value);
              }}
              onSearch={async (value: string) => {
                const response = await peopleApiService.fetch(
                  new URLSearchParams({
                    page: '1',
                    page_size: '1',
                    search: value,
                  })
                );
                setSearchedPeopleList(response.results);
              }}
              filterOption={false}
              options={searchedPeopleList.map(people => ({
                label: people.name,
                value: people.id,
              }))}
              style={{ width: '100%' }}
            />
          </Form.Item> */}
          {/* Enhanced Cast Editor */}
          <Form.Item label="Enhanced Cast">
            <EnhancedCastEditor
              value={enhancedCast}
              onChange={setEnhancedCast}
              peopleList={searchedPeopleList}
              onSearchPeople={searchPeopleForEnhancedCast}
            />
          </Form.Item>
          <Form.Item
            name="genres"
            label="Genres"
            rules={[{ required: false, message: 'Please Select Genres' }]}
          >
            <Select
              mode="multiple"
              showSearch
              placeholder="Search and select Genres"
              value={selectedGenres}
              onChange={value => {
                setSelectedGenres(value);
                setGenreList(initialGenreList);
              }}
              onSearch={searchGenres}
              onSelect={async id => {
                if (!initialGenreList.some(genre => genre.id === id)) {
                  const response = await genreApiService.fetch(
                    new URLSearchParams({
                      page: '1',
                      page_size: '1',
                      id: id.toString(),
                    })
                  );
                  const fetchedGenre = response.results[0];
                  if (fetchedGenre) {
                    // initialGenreList에 추가
                    setInitialGenreList(prev => [...prev, fetchedGenre]);

                    // genreList에도 추가
                    setGenreList(prev => [...prev, fetchedGenre]);
                  }
                }
              }}
              onBlur={() => {
                setGenreList(initialGenreList);
              }}
              filterOption={false}
              options={genreList.map(genre => ({
                label: genre.name,
                value: genre.id,
              }))}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item name="series" label="Series">
            <div>
              {!series ? (
                <Button
                  type="link"
                  onClick={() => window.open('/series', '_blank', 'noopener,noreferrer')}
                >
                  Edit
                </Button>
              ) : null}
              <div style={{ marginTop: '8px' }}>
                {series ? (
                  <Tag
                    color="blue"
                    className="season-tag"
                    onClick={() =>
                      window.open(`/series/edit/${series.id}`, '_blank', 'noopener,noreferrer')
                    }
                  >
                    {series.title}
                  </Tag>
                ) : null}
              </div>
            </div>
          </Form.Item>

          <Divider />

          {/* SEO 제목 */}
          <Component.I18nFormFields
            fieldName="seo_title"
            required={false}
            fieldType="input"
            value={i18nSeoTitle}
            onChange={value => {
              setI18nSeoTitle(value);
            }}
          />

          {/* SEO 설명 */}
          <Component.I18nFormFields
            fieldName="seo_synopsis"
            required={false}
            fieldType="textarea"
            value={i18nSeoSynopsis}
            onChange={value => {
              setI18nSeoSynopsis(value);
            }}
          />

          {/* SEO 키워드 */}
          <Component.I18nFormFields
            fieldName="seo_keyword"
            required={false}
            fieldType="input"
            value={i18nSeoKeyword}
            onChange={value => {
              setI18nSeoKeyword(value);
            }}
          />
          <Divider />

          <Form.Item name="production_year" label="Production Year">
            <CustomInputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="score" label="Score">
            <CustomInputNumber style={{ width: '100%' }} min={0} precision={1} />
          </Form.Item>
          <Form.Item name="running_time" label="Running Time">
            <CustomInputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="release_weekday" label="Release Weekday">
            <WeekdayCheckbox />
          </Form.Item>
          {/* <Form.Item name="odx_legacy_id" label="ODX Legacy ID">
            <Input />
          </Form.Item> */}
          <Form.Item name="total_episodes" label="Total Episodes">
            <CustomInputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>

          {/* ODK Meta Tag Editable */}
          <Form.Item name="odk_meta_tag" label="ODK Meta Tag">
            <EditableOdkMetaTag />
          </Form.Item>

          {form.getFieldValue('program_meta') && (
            <>
              <h2>ODX Meta</h2>
              <Form.Item name={['program_meta', 'program_id']} label="Program ID">
                <Input readOnly />
              </Form.Item>
              <Form.Item name={['program_meta', 'program_slug']} label="Program Slug">
                <Input readOnly />
              </Form.Item>
              {Array.isArray(form.getFieldValue('program_meta')?.program_image) &&
                form
                  .getFieldValue('program_meta')
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  ?.program_image.map((image: any, index: number) => (
                    <div key={index}>
                      <img src={image['@url']} alt="program_image" style={{ width: '100px' }} />
                      <Form.Item
                        name={['program_meta', 'program_image', index, '@url']}
                        label={`Program Image - ${image['@type']}`}
                      >
                        <Input readOnly />
                      </Form.Item>
                    </div>
                  ))}
              <Form.Item name={['program_meta', 'program_enable']} label="Program Enable">
                <Input readOnly />
              </Form.Item>
              <Form.Item name={['program_meta', 'ingest_source']} label="Ingest Source">
                <Input readOnly />
              </Form.Item>
              <Form.Item
                name={['program_meta', 'program_release_start']}
                label="Program Release Start"
              >
                <Input readOnly />
              </Form.Item>
              <Form.Item name={['program_meta', 'program_release_end']} label="Program Release End">
                <Input readOnly />
              </Form.Item>
              <Form.Item
                name={['program_meta', 'program_publish_start']}
                label="Program Publish Start"
              >
                <Input readOnly />
              </Form.Item>
              <Form.Item name={['program_meta', 'program_publish_end']} label="Program Publish End">
                <Input readOnly />
              </Form.Item>
              <Form.Item name={['program_meta', 'program_cp']} label="Program CP">
                <Input readOnly />
              </Form.Item>
            </>
          )}
        </Form>
      </div>
      <div className="season-detail-update-button">
        <Component.ColorButtonComponent text={'Update'} onClick={handleUpdateSeasonWithI18n} />
      </div>
    </div>
  );
};

export default SeasonDetail;
